import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, CheckCircle2, Shield, AlertTriangle, Printer, Download } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const CARD   = "#161618";

const EFFECTIVE_DATE = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const CHECKBOXES = [
  { id: "cb_lead_gen",     label: "I understand Hylas is a driver recruiting and marketing service only — Hylas does not verify CDL, obtain MVR, PSP, Clearinghouse, or background checks, does not act as employer or motor carrier, and does not guarantee any hire. All DOT qualification (49 CFR 391) and hiring decisions are my sole responsibility." },
  { id: "cb_fee",          label: "I agree to pay a variable placement fee set by live auction at hylascompanion.com/market/pricing. Minimum floors: Semi Truck $1,500–$2,100+ (Company Driver $1,500 / Rental $1,700 / Lease Purchase $1,900 / Owner Operator $2,100). Box Truck $800–$1,400+. My winning bid is the final fee. Payment options: (i) Standard 50/50 — 50% on First Dispatch + 50% due 14 days after; (ii) One-Time — 100% on First Dispatch ($100–$600 discount depending on truck type, fully earned, non-refundable); (iii) Weekly Flex — from $55/wk (Box) / $95/wk (Semi), 9/17/35/52-wk plans, 4-wk minimum, billed Mon 12am–Sun 11:59pm CST, current week non-refundable, notify support@hylascompanion.us within 24h of separation. Late fees: $300/day Semi / $150/day Box as liquidated damages, capped at 100% per driver, 1.5%/month after 30 days. Hylas has Immediate Remarketing Rights if any payment is 7+ days overdue." },
  { id: "cb_replacement",  label: "I understand the Replacement Credit (Section 3B): if a driver quits, abandons, or is terminated within 7 days of first dispatch AND I have documented net loss, I may claim a credit capped at the amount I paid Hylas for that driver, applicable only to the next placement fee. Credit expires in 90 days. No stacking, no cash refunds. Applies only to Standard 50/50 and One-Time plans." },
  { id: "cb_45day",        label: "I acknowledge the 45-Day Ownership & Non-Circumvention: any driver submitted by Hylas is protected for 45 days from transmission date. Hiring, leasing, or using that driver in any capacity within 45 days — directly or via affiliate — triggers the full $1,500 fee." },
  { id: "cb_marketing",    label: "I authorize Hylas to market my company as a 'Partner Fleet/Carrier' for driver recruiting purposes. I understand I am independent from Hylas and shall not represent Hylas as my employer or carrier. I shall not offer referral bonuses to Hylas drivers during the Ownership Window or first 90 days. Breach = $1,500 per solicitation." },
  { id: "cb_liability",    label: "I understand that upon first dispatch, ALL employer liability transfers to my company — including pay, taxes, workers' comp, insurance, DOT/FMCSA compliance, ELD, and DOL obligations. I agree to indemnify, defend, and hold harmless Hylas from any claims, DOT audits, fines, accidents, or wage claims arising from the driver's employment under my authority." },
  { id: "cb_data",         label: "I acknowledge that Hylas has driver authorization to transmit applications per the Multi-Carrier Lead Authorization. I will use driver data only for employment evaluation, store it securely, and will not resell it. Fees and terms of this Agreement are confidential." },
];

export function CarrierAgreementModal({ token, carrierName, onAgreed, onClose }) {
  const [checked, setChecked]         = useState({});
  const [signConsent, setSignConsent] = useState(false);
  const [sigName, setSigName]         = useState("");
  const [company, setCompany]         = useState(carrierName || "");
  const [submitting, setSubmitting]   = useState(false);
  const scrollRef                     = useRef(null);

  const allChecked = CHECKBOXES.every(c => checked[c.id]);
  const canSign    = allChecked && signConsent && sigName.trim().length >= 2;

  async function handleSign() {
    if (!canSign) return;
    setSubmitting(true);
    try {
      await api.marketSignAgreement(token, { signature_name: sigName.trim(), company_name: company.trim() });
      toast.success("Agreement signed — you're all set to bid!");
      onAgreed({ signed_at: new Date().toISOString(), signature_name: sigName.trim(), company_name: company.trim() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed — please try again.");
    } finally { setSubmitting(false); }
  }

  function handlePrint() {
    const win = window.open("", "_blank");
    if (!win) return;
    const content = document.getElementById("hca-agreement-content").innerHTML;
    const sigBlock = `
      <div style="margin-top:40px;padding-top:24px;border-top:2px solid #FF4D00">
        <p style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#FF4D00;margin:0 0 8px;font-weight:bold">
          Agreed via Clickwrap E-Signature — ESIGN Act (15 U.S.C. § 7001)
        </p>
        <p style="font-size:9px;color:#888;margin:0 0 16px">
          ☑ I have read and agree to the Fleet Client Agreement &nbsp;·&nbsp; IP captured at signing &nbsp;·&nbsp; ${EFFECTIVE_DATE}
        </p>
        <table style="width:100%;font-family:Arial,sans-serif;font-size:12px;border-collapse:collapse">
          <tr>
            <td style="width:50%;padding-right:32px;vertical-align:top">
              <p style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#666;margin:0 0 4px">Hylas Companion</p>
              <p style="font-size:10px;color:#555;margin:0">Andrej Sasaroga DBA Hylas Companion</p>
              <p style="font-size:9px;color:#999;margin:6px 0 0">Date: ${EFFECTIVE_DATE}</p>
            </td>
            <td style="width:50%;vertical-align:top">
              <p style="font-size:9px;text-transform:uppercase;letter-spacing:0.12em;color:#666;margin:0 0 4px">Client: ${company || "[Client Legal Name]"}</p>
              <p style="font-size:14px;font-style:italic;font-weight:bold;margin:0;color:#1a1a1a">${sigName || "—"}</p>
              <p style="font-size:9px;color:#999;margin:4px 0 0">Typed Signature &nbsp;·&nbsp; Date: ${EFFECTIVE_DATE}</p>
            </td>
          </tr>
        </table>
      </div>`;
    win.document.write(`<!DOCTYPE html><html><head><title>Fleet Client Agreement — Andrej Sasaroga DBA Hylas Companion</title>
    <style>
      @page{margin:2cm}
      body{font-family:Arial,sans-serif;font-size:11px;line-height:1.6;color:#1a1a1a;margin:0;padding:0}
      h1{font-size:18px;font-weight:bold;margin-bottom:4px;color:#1a1a1a}
      h2{font-size:13px;text-transform:uppercase;letter-spacing:0.08em;margin:20px 0 8px;padding-bottom:4px;border-bottom:2px solid #FF4D00;color:#1a1a1a}
      h3{font-size:11px;font-weight:bold;margin:12px 0 4px}
      .header{border-bottom:3px solid #FF4D00;padding-bottom:16px;margin-bottom:20px}
      .logo{font-size:11px;text-transform:uppercase;letter-spacing:0.15em;color:#FF4D00;font-weight:bold;margin-bottom:4px}
      .confidential{font-size:9px;text-transform:uppercase;letter-spacing:0.15em;color:#999;margin-top:4px}
      .tag{display:inline-block;background:#FFF3E0;color:#FF4D00;padding:2px 8px;font-size:9px;text-transform:uppercase;letter-spacing:0.12em;border:1px solid #FFCC80;margin-left:8px}
      ul{margin:4px 0;padding-left:16px}
      li{margin:3px 0}
      .highlight{background:#FFF8E1;border-left:3px solid #FF4D00;padding:8px 12px;margin:8px 0}
    </style></head><body>
    <div class="header">
      <div class="logo">Andrej Sasaroga DBA Hylas Companion</div>
      <h1>Fleet Client Agreement</h1>
      <p style="margin:0;font-size:10px;color:#666">Effective: ${EFFECTIVE_DATE} · Confidential</p>
    </div>
    ${content}
    ${sigBlock}
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          className="relative w-full max-w-3xl flex flex-col"
          style={{ background: DARK, border: `1px solid rgba(255,77,0,0.3)`, maxHeight: "94vh" }}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-7 py-5 shrink-0" style={{ borderBottom: `2px solid ${ACCENT}` }}>
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Andrej Sasaroga DBA Hylas Companion</p>
              <h2 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">Fleet Client Agreement</h2>
              <p className="font-head text-xs text-white/30 mt-0.5">Driver Recruiting & Marketing Services · {EFFECTIVE_DATE}</p>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <button onClick={handlePrint} title="Print agreement"
                className="p-1.5 transition-colors hover:text-white/80 text-white/30"
              ><Printer className="w-4 h-4" /></button>
              <button onClick={onClose} className="p-1.5 text-white/30 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>
          </div>

          {/* Scrollable agreement body */}
          <div ref={scrollRef} className="overflow-y-auto px-7 py-6 space-y-6 flex-1 text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>
            <div id="hca-agreement-content">
              {/* Preamble */}
              <div className="p-4 mb-2" style={{ background: "rgba(255,77,0,0.06)", border: "1px solid rgba(255,77,0,0.18)" }}>
                <p className="font-head text-xs text-white/50 mb-1 uppercase tracking-[0.2em]">Between:</p>
                <p><strong className="text-white">Andrej Sasaroga DBA Hylas Companion</strong> ("Hylas") — contact@hylascompanion.com — Driver Recruiting &amp; Marketing Service</p>
                <p className="mt-1">and</p>
                <p className="mt-1"><strong className="text-white">{company || "[Client Legal Name]"}</strong> ("Client")</p>
                <p className="mt-2 text-xs text-white/40">Hylas is a driver recruiting and marketing service only.</p>
              </div>

              {/* Section 2: Services */}
              <AgreementSection title="2. Services — Lead Generation Only" accent={ACCENT}>
                <p>Hylas provides CDL driver sourcing, marketing, and initial non-regulated pre-screening based on driver self-reported data. Hylas markets Client's open positions and refers candidates.</p>
                <div className="p-3 my-3" style={{ background: "rgba(255,77,0,0.05)", border: "1px solid rgba(255,77,0,0.2)" }}>
                  <p className="font-head text-[10px] tracking-[0.15em] mb-2" style={{ color: ACCENT }}>Hylas does not:</p>
                  <ul className="space-y-1 list-none pl-0">
                    {["Verify CDL, obtain MVR, PSP, Clearinghouse, or background checks",
                      "Perform DOT compliance verification or drug testing",
                      "Act as motor carrier, employer, C/TPA, or consumer reporting agency",
                      "Guarantee any hire"].map(t => (
                      <li key={t} className="flex gap-2 text-xs text-white/60"><span style={{ color: ACCENT }} className="mt-0.5 shrink-0">×</span><span>{t}</span></li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-white/50">All qualification per 49 CFR 391, safety review, drug testing, Clearinghouse queries, and hiring decisions are the <strong className="text-white/80">sole responsibility of Client</strong>.</p>
              </AgreementSection>

              {/* Section 3: Fee Schedule */}
              <AgreementSection title={`3. Fee Schedule — Variable Placement Fee (Live at hylascompanion.com/market/pricing · Floor Prices Effective ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })})`} accent={ACCENT}>
                {/* 3a: Floor Prices */}
                <p className="text-xs font-black text-white/80 uppercase tracking-[0.15em] mt-1 mb-1">a) Base Floor Prices — No Maximum Cap, Bid Higher to Win</p>
                <p className="text-xs text-white/55 mb-2">Placement Fee to Hylas is variable and set by live auction. Client's winning bid is the final fee. Minimum floors:</p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="p-2.5" style={{ background: "rgba(245,200,66,0.05)", border: "1px solid rgba(245,200,66,0.15)" }}>
                    <p className="text-[10px] font-black uppercase tracking-[0.15em] mb-1.5" style={{ color: GOLD }}>Semi Truck ($1,500–$2,100+ Floor)</p>
                    {[["Company Driver","Floor $1,500","Min Driver Pay $0.70 CPM"],["Rental","Floor $1,700","Min 85% Revenue"],["Lease Purchase","Floor $1,900","Min 85% Revenue"],["Owner Operator","Floor $2,100","Min 88% Revenue"]].map(([t,f,m]) => (
                      <div key={t} className="flex justify-between text-[10px] text-white/55 border-b border-white/5 pb-0.5 mb-0.5">
                        <span>{t}</span><span style={{ color: GOLD }}>{f}</span><span className="text-white/35">{m}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-2.5" style={{ background: "rgba(245,200,66,0.05)", border: "1px solid rgba(245,200,66,0.15)" }}>
                    <p className="text-[10px] font-black uppercase tracking-[0.15em] mb-1.5" style={{ color: GOLD }}>Box Truck ($800–$1,400+ Floor)</p>
                    <p className="text-[10px] text-white/55">Floor range $800–$1,400+ depending on pay program and package. No maximum cap.</p>
                    <p className="text-[10px] text-white/35 mt-2 italic">System displays "4 CARRIERS ARE COMPETING FOR THIS DRIVER RIGHT NOW" as market indicator. Higher bid increases win rate. Fee locked at winning bid per email/ATS timestamp.</p>
                  </div>
                </div>
                {/* 3b: Payment Structure */}
                <p className="text-xs font-black text-white/80 uppercase tracking-[0.15em] mb-1">b) Payment Structure — Client Elects One at Checkout</p>
                <ul className="space-y-2 list-none pl-0 mb-3">
                  {[
                    ["i) Standard 50/50 (Default)", "50% due on Driver Hire Day (First Dispatch), 50% due 14 days after First Dispatch. Replacement Credit per Section 3B applies."],
                    ["ii) One-Time Payment", "100% due on Driver Hire Day (First Dispatch). Includes $100–$600 discount vs Standard 50/50 depending on truck type and bidding (e.g., $1,500 Standard = $1,200 One-Time). Replacement Credit per Section 3B applies. Fully earned, non-refundable."],
                    ["iii) Hylas Weekly Flex", "From $55/week (Box) / $95/week (Semi) with auto-stop. Plans: 9 Wks / 17 Wks (Popular) / 35 Wks / 52 Wks. 4-week minimum commitment. Billing cycle Mon 12:00am–Sun 11:59pm CST. If Driver leaves any day Mon–Sun, that week is non-refundable and fully earned. Client must notify Hylas within 24h of separation via email to support@hylascompanion.us; failure to notify = following week billable. Subscription auto-stops. No Replacement Credit for Weekly Flex."],
                  ].map(([plan, desc]) => (
                    <li key={plan} className="flex gap-2 text-xs">
                      <span className="shrink-0 font-black text-white min-w-[110px]">{plan}:</span>
                      <span className="text-white/55">{desc}</span>
                    </li>
                  ))}
                </ul>
                {/* Weekly Flex floor pricing table */}
                <div className="mb-3 p-2.5" style={{ background: "rgba(245,200,66,0.04)", border: "1px solid rgba(245,200,66,0.12)" }}>
                  <p className="text-[10px] font-black uppercase tracking-[0.15em] mb-1.5" style={{ color: GOLD }}>Weekly Flex Floor Pricing (No Maximum Cap)</p>
                  <div className="grid grid-cols-2 gap-1.5 text-[9px]">
                    {[["Semi Truck","9 Wks $275–$375","17 Wks $185–$255","35 Wks $120–$165","52 Wks $95–$135"],["Box Truck","9 Wks $150–$250","17 Wks $100–$170","35 Wks $65–$110","52 Wks $55–$90"]].map(([type,...rows])=>(
                      <div key={type}>
                        <p className="font-black text-white/70 mb-0.5">{type}</p>
                        {rows.map(r=><p key={r} className="text-white/40">{r}</p>)}
                      </div>
                    ))}
                  </div>
                </div>
                {/* 3c–3f */}
                <ul className="space-y-2 list-none pl-0">
                  {[
                    ["c) First Dispatch Definition", "First load dispatched to Driver by Client OR Day 7 after orientation start date, whichever occurs first. Orientation start = first day Driver appears at Client facility or logs into ELD. Fee is fully earned upon First Dispatch."],
                    ["d) Late Fees & Remedies", "$300/day per Semi Truck placement and $150/day per Box Truck placement as liquidated damages (not interest), capped at 100% of Placement Fee per driver. 1.5%/month interest after 30 days overdue on total outstanding. Hylas may suspend all new driver transmissions and pause active bids during any arrears over 7 days."],
                    ["e) Immediate Remarketing Rights", "If Client fails to pay any amount when due within 7 days of due date — including if Driver has already started driving and is 7 days late — Hylas has the immediate right to return Driver to Hylas Market and re-market, transmit and place Driver with another fleet, even while Driver is still dispatched with original Client. Due dates: (i) Standard 50/50: Day 0 and Day 14; (ii) One-Time: Day 0; (iii) Weekly Flex: each Monday. Failure to pay any weekly installment within 7 days triggers same rights. If Driver separates from Client for any reason after First Dispatch, Hylas may immediately re-market Driver. Client remains liable for original fee plus late fees and interest, and shall not interfere with Hylas re-marketing efforts. This right is based on Driver's 12-month Application Sharing Authorization."],
                    ["f) Pricing Page Control", "Live simulator at hylascompanion.com/market/pricing controls. In case of conflict between this Agreement and Pricing Page, winning bid confirmation (email/ATS timestamp) on transmission date controls. Floor prices may be updated with 7 days notice."],
                  ].map(([plan, desc]) => (
                    <li key={plan} className="flex gap-2 text-xs">
                      <span className="shrink-0 font-black text-white min-w-[110px]">{plan}:</span>
                      <span className="text-white/55">{desc}</span>
                    </li>
                  ))}
                </ul>
              </AgreementSection>

              {/* Section 3B: Replacement Credit */}
              <AgreementSection title="3B. Replacement Credit — First Week Net Loss" accent={ACCENT}>
                <p>If a driver quits, abandons, or is terminated for cause within <strong className="text-white">7 calendar days</strong> from first dispatch, and Client is in documented net loss (expenses &gt; revenue), Client is entitled to a refill/credit balance.</p>
                <ul className="mt-2 space-y-1.5 list-none pl-0">
                  {[
                    "Client must notify Hylas within 48 hours of departure and provide settlement sheets / expense proof showing net loss.",
                    "Credit equals documented net loss, capped at amount actually paid to Hylas for that driver.",
                    "Credit applies only to next driver placement fee — non-refundable, non-cash, expires after 90 days.",
                    "Applies ONLY to Standard 50/50 and One-Time plans. Weekly Flex has no replacement credit.",
                    "One credit per failed placement. No stacking.",
                  ].map((t, i) => (
                    <li key={i} className="flex gap-2 text-xs text-white/60"><span style={{ color: GOLD }} className="mt-0.5 shrink-0 font-black">{String.fromCharCode(97+i)})</span><span>{t}</span></li>
                  ))}
                </ul>
              </AgreementSection>

              {/* Section 4: 45-Day */}
              <AgreementSection title="4. 45-Day Ownership & Non-Circumvention" accent={ACCENT}>
                <div className="p-3 my-2" style={{ background: "rgba(255,77,0,0.05)", border: "1px solid rgba(255,77,0,0.15)" }}>
                  <p>Any driver submitted via Hylas and transmitted to Client is protected for <strong className="text-white">45 days</strong> from transmission date. Hiring, leasing, or using that driver in any capacity within 45 days — including via affiliate, other recruiter, or as owner-op — triggers the full placement fee.</p>
                  <p className="mt-2 text-xs text-white/50">Proof of submission = Hylas email/ATS timestamp.</p>
                </div>
              </AgreementSection>

              {/* Section 5: Partner Marketing */}
              <AgreementSection title="5. Partner Marketing" accent={ACCENT}>
                <ul className="space-y-1.5 list-none pl-0">
                  {[
                    "Client is independent and separate from Hylas.",
                    "Client authorizes Hylas to market Client as a 'Partner Fleet/Carrier' for recruiting purposes only.",
                    "Client shall not represent Hylas as its employer or carrier.",
                  ].map((t, i) => (
                    <li key={i} className="flex gap-2 text-sm text-white/60"><span style={{ color: ACCENT }} className="mt-0.5 shrink-0">›</span><span>{t}</span></li>
                  ))}
                </ul>
              </AgreementSection>

              {/* Section 6: No Referral */}
              <AgreementSection title="6. No Referral Solicitation" accent={ACCENT}>
                <p>Client shall not offer referral bonuses to Hylas drivers to refer other Hylas drivers during the 45-Day Ownership Window and first 90 days of employment. <strong className="text-white">Breach = $1,500 per solicitation.</strong></p>
              </AgreementSection>

              {/* Section 7: Client Responsibilities */}
              <AgreementSection title="7. Client Responsibilities" accent={ACCENT}>
                <ul className="space-y-1.5 list-none pl-0">
                  {[
                    "Client acknowledges Hylas does not obtain consumer reports (MVR, PSP, DAC, Clearinghouse).",
                    "Client is solely responsible for FCRA compliance, FMCSA Clearinghouse queries, MVR, PSP, 49 CFR 391.23 checks, and all DOT compliance.",
                    "Client represents it has valid authority and insurance to hire drivers, directly or under a partner carrier's authority.",
                  ].map((t, i) => (
                    <li key={i} className="flex gap-2 text-sm text-white/60"><span style={{ color: ACCENT }} className="mt-0.5 shrink-0">›</span><span>{t}</span></li>
                  ))}
                </ul>
              </AgreementSection>

              {/* Section 8: Liability */}
              <AgreementSection title="8. Liability Transfer & Indemnification" accent={ACCENT}>
                <p>Upon <strong className="text-white">first dispatch</strong>, all employer liability transfers to Client — including pay, taxes, workers' comp, equipment, insurance, DOT/FMCSA, ELD, and DOL. <strong className="text-white">Hylas bears zero employer liability after placement.</strong></p>
                <p className="mt-3">Client shall <strong className="text-white">indemnify, defend, and hold harmless</strong> Hylas from any claims, DOT audits, fines, accidents, wage claims, or lawsuits arising from the driver's employment or operation under Client.</p>
              </AgreementSection>

              {/* Section 9: Data */}
              <AgreementSection title="9. Data & Confidentiality" accent={ACCENT}>
                <ul className="space-y-1.5 list-none pl-0">
                  {[
                    "Client acknowledges Hylas has driver authorization to transmit applications per the Multi-Carrier Lead Authorization.",
                    "Client shall use driver data only for employment evaluation, store securely, and shall not resell.",
                    "Placement fees and all terms of this Agreement are confidential.",
                  ].map((t, i) => (
                    <li key={i} className="flex gap-2 text-xs text-white/60"><span style={{ color: "rgba(255,255,255,0.3)" }} className="mt-0.5 shrink-0">›</span><span>{t}</span></li>
                  ))}
                </ul>
              </AgreementSection>

              {/* Section 10: Default */}
              <AgreementSection title="10. Default" accent={ACCENT}>
                <p>If payment is not received within <strong className="text-white">7 days</strong> after written email notice of default, all outstanding fees become immediately due and Hylas may pursue collection and all available legal remedies, including attorney fees.</p>
              </AgreementSection>

              {/* Section 11: General */}
              <AgreementSection title="11. General Terms" accent={ACCENT}>
                <ul className="space-y-1.5 list-none pl-0">
                  {[
                    ["a) Term:", "Month-to-month, 7-day written email termination. Sections 4, 6, 7, 8, and 10 survive termination."],
                    ["b) No Guarantee:", "Hylas does not guarantee driver performance or hire outcomes."],
                    ["c) No Joint Employer/Partnership:", "Hylas and Client are independent. No employment or partnership relationship is created."],
                    ["d) Entire Agreement:", "This document is the complete and binding agreement between the parties."],
                    ["e) Governing Law & Arbitration:", "Binding arbitration in Cook County, Illinois under FAA rules, individual basis only. Prevailing party gets attorney fees."],
                    ["f) E-SIGN:", "Electronic signatures are valid and binding under the ESIGN Act (15 U.S.C. § 7001)."],
                  ].map(([label, text], i) => (
                    <li key={i} className="flex gap-2 text-xs text-white/60">
                      <span className="shrink-0 font-black text-white/50">{label}</span>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </AgreementSection>
            </div>

            {/* Checkboxes */}
            <div className="mt-2 space-y-3 pt-5" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="font-head text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Please confirm each term before signing:</p>
              {CHECKBOXES.map(c => (
                <label key={c.id} className="flex gap-3 cursor-pointer group">
                  <button
                    role="checkbox"
                    aria-checked={!!checked[c.id]}
                    data-testid={`agreement-${c.id}`}
                    onClick={() => setChecked(p => ({ ...p, [c.id]: !p[c.id] }))}
                    className="mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center transition-all"
                    style={{
                      border: `1px solid ${checked[c.id] ? ACCENT : "rgba(255,255,255,0.2)"}`,
                      background: checked[c.id] ? "rgba(255,77,0,0.15)" : "transparent",
                    }}
                  >
                    {checked[c.id] && <CheckCircle2 className="w-3 h-3" style={{ color: ACCENT }} />}
                  </button>
                  <span className="text-xs leading-relaxed" style={{ color: checked[c.id] ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.45)" }}>
                    {c.label}
                  </span>
                </label>
              ))}
            </div>

            {/* Clickwrap signature block */}
            <div className="pt-5 space-y-4" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="font-head text-xs uppercase tracking-[0.25em] text-white/40">E-Signature — Clickwrap</p>

              {/* Company name */}
              <div>
                <label className="block font-head text-[10px] uppercase tracking-[0.2em] text-white/30 mb-1.5">
                  Company / Fleet Legal Name <span style={{ color: ACCENT }}>*</span>
                </label>
                <input
                  data-testid="agreement-company-name"
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                  placeholder="Your company legal name"
                  className="w-full px-3 py-2.5 bg-transparent text-white text-sm outline-none"
                  style={{ border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.03)" }}
                />
              </div>

              {/* Typed signature */}
              <div>
                <label className="block font-head text-[10px] uppercase tracking-[0.2em] text-white/30 mb-1.5">
                  Typed Signature <span style={{ color: ACCENT }}>*</span>
                  <span className="ml-2 normal-case tracking-normal text-white/20 text-[9px]">(type your full name — this serves as your legal e-signature)</span>
                </label>
                <input
                  data-testid="agreement-signature-name"
                  value={sigName}
                  onChange={e => setSigName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-3 py-2.5 bg-transparent text-sm outline-none"
                  style={{
                    border: `1px solid ${sigName.trim().length >= 2 ? "rgba(255,77,0,0.5)" : "rgba(255,255,255,0.15)"}`,
                    background: "rgba(255,255,255,0.03)",
                    fontStyle: sigName ? "italic" : "normal",
                    fontFamily: sigName ? "Georgia, serif" : "inherit",
                    color: sigName ? "#fff" : "rgba(255,255,255,0.4)",
                    fontSize: sigName ? "15px" : "13px",
                  }}
                />
              </div>

              {/* Date (auto) */}
              <div className="flex items-center gap-3 text-xs text-white/30">
                <span className="font-head uppercase tracking-[0.15em]">Date:</span>
                <span className="text-white/60 font-medium">{EFFECTIVE_DATE}</span>
                <span className="text-white/20">(auto-filled)</span>
              </div>

              {/* Consent checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div
                  data-testid="agreement-consent-checkbox"
                  onClick={() => setSignConsent(v => !v)}
                  className="mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center transition-all"
                  style={{
                    background: signConsent ? ACCENT : "transparent",
                    border: `2px solid ${signConsent ? ACCENT : "rgba(255,255,255,0.2)"}`,
                  }}>
                  {signConsent && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>}
                </div>
                <p className="text-sm text-white/60 group-hover:text-white/80 transition-colors leading-snug">
                  I have read and agree to the <strong className="text-white">Fleet Client Agreement</strong>. I understand Hylas is a recruiting and marketing service only, and all terms above apply to my company.
                </p>
              </label>

              {/* ESIGN notice */}
              {signConsent && sigName.trim().length >= 2 && (
                <div className="px-4 py-2.5 flex items-center gap-3" style={{ background: "rgba(255,77,0,0.05)", border: "1px solid rgba(255,77,0,0.15)" }}>
                  <Shield className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                  <p className="text-xs text-white/50">
                    By clicking "Sign &amp; Agree", <strong className="text-white">{sigName}</strong> agrees this typed name constitutes a valid e-signature under the ESIGN Act (15 U.S.C. § 7001). Your IP address, email, timestamp, and consent items will be recorded.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-7 py-4 shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.07)", background: "#0d0d0d" }}>
            <p className="text-xs text-white/25 text-center sm:text-left">
              {!allChecked
                ? `${CHECKBOXES.filter(c => !checked[c.id]).length} item${CHECKBOXES.filter(c => !checked[c.id]).length !== 1 ? "s" : ""} left to confirm`
                : !sigName.trim()
                ? "Type your full name above to sign"
                : "Ready to sign"}
            </p>
            <div className="flex items-center gap-3">
              <button onClick={onClose}
                className="font-head text-xs uppercase tracking-[0.2em] px-5 py-2.5 border border-white/10 text-white/40 hover:text-white/70 transition-colors"
              >Cancel</button>
              <button
                data-testid="agreement-sign-btn"
                onClick={handleSign}
                disabled={!canSign || submitting}
                className="font-head text-xs font-black uppercase tracking-[0.25em] px-6 py-2.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: canSign ? ACCENT : "rgba(255,77,0,0.3)", color: "white" }}
              >
                {submitting ? "Signing…" : "Sign & Agree →"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AgreementSection({ title, accent, children }) {
  return (
    <div className="space-y-2">
      <h3 className="font-head text-xs font-black uppercase tracking-[0.12em] pb-1.5"
        style={{ color: accent, borderBottom: `1px solid rgba(255,77,0,0.2)` }}>
        {title}
      </h3>
      <div className="leading-relaxed space-y-2 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
        {children}
      </div>
    </div>
  );
}
